/**
 * Davnai brand tokens: the ONLY place colours are defined.
 * Everything else uses theme keys (e.g. "primary.main") or the exports below, never colour codes.
 * A test fails if a colour code appears anywhere outside src/theme/.
 * Contrast of every text/background pair is checked by src/theme/__tests__/contrast.test.ts.
 *
 * Brand rules (docs/brand.md):
 *   Warm ivory = light surfaces; charcoal = text and dark workspaces.
 *   Soft yellow = contained actions and selected navigation, always with charcoal text.
 *   Warm gold = editorial/AI highlights; status hues remain semantic.
 *   Media palettes and official logos below remain separate from product chrome.
 */

export const brand = {
  name: "Davnai",
  tagline: "AI Content Engine",
  promise: "Turn your ideas, recordings and thoughts into high-quality content.",
} as const;

/** Product chrome, separate from people's media palettes and platform logos. */
export const studioBrand = {
  action: "#FFDA83",
  actionHover: "#F5CB69",
  onAction: "#242520",
  focus: "#926300",
  heroLight: "linear-gradient(90deg, #FBF6EA 0%, rgba(251,246,234,.98) 27%, rgba(251,246,234,.88) 43%, rgba(251,246,234,0) 75%)",
  heroDark: "linear-gradient(90deg, rgba(18,21,17,.97) 0%, rgba(18,21,17,.9) 30%, rgba(18,21,17,.55) 52%, rgba(18,21,17,0) 80%)",
  heroPaper: "#FBF6EA",
  heroInk: "#242520",
  heroMuted: "#58594F",
  heroAccent: "#805500",
  cardFade: "linear-gradient(0deg, rgba(18,21,17,.65), transparent 70%)",
  warmTint: "#FFF4D8",
  imageTint: "#EDF2E7",
  writingTint: "#EBF0F3",
  socialTint: "#FBEEEE",
} as const;

/** Shared workspace widths: keep navigation readable while leaving room for laptop canvases. */
export const workspaceLayout = {
  sidebarWidth: 200,
  sidebarRailWidth: 64,
  dashboardAsideWidth: 240,
  dashboardAsideWideWidth: 260,
} as const;

export const palette = {
  light: {
    primary: { main: "#262622", dark: "#171815", light: "#59594F", contrastText: "#FFFDF7" },
    secondary: { main: "#805500", dark: "#654200", light: "#B58527", contrastText: "#FFFDF7" },
    success: { main: "#047857", contrastText: "#FFFFFF" },
    warning: { main: "#B45309", contrastText: "#FFFFFF" },
    error: { main: "#B91C1C", contrastText: "#FFFFFF" },
    info: { main: "#1D4ED8", contrastText: "#FFFFFF" },
    background: { default: "#FBF8F1", paper: "#FFFDF8" },
    text: { primary: "#242520", secondary: "#65655C" },
    divider: "#E9E4D9",
  },
  dark: {
    primary: { main: "#FFDA83", dark: "#EAC05F", light: "#FFE8B3", contrastText: "#20211D" },
    secondary: { main: "#E5C783", dark: "#D1B16A", light: "#F5E0AC", contrastText: "#20211D" },
    success: { main: "#34D399", contrastText: "#0B1024" },
    warning: { main: "#FBBF24", contrastText: "#0B1024" },
    error: { main: "#F87171", contrastText: "#0B1024" },
    info: { main: "#7DD3FC", contrastText: "#0B1024" },
    background: { default: "#191A17", paper: "#23241F" },
    text: { primary: "#F7F3E9", secondary: "#BAB9AD" },
    divider: "#414239",
  },
} as const;

/** Emails (approved canvas board 71): the header line, the footer band and its links. Emails are always light. */
export const emailLook = {
  outside: "#EFEAE0",
  gold: "#FFDA83",
  footer: "#F6F2E9",
  line: "#E7E1D3",
  link: "#805500",
  muted: "#4A4A42",
  kicker: "#805500",
  buttonBorder: "#D9D2C2",
} as const;

/** The example creator's brand on the public pages' conversation (board 73: "ADA" on her link card). */
export const siteDemoLook = { brand: "#2F5D50", brandInk: "#FFFFFF", gold: "#FFDA83", tint: "#FFF4D6", phone: "#2B2D27" } as const;
/** Home page (approved canvas board 74): the numbered hero steps, the builder card, the demo's bubbles and the dark tiles. */
export const homeLook = {
  stepNumber: "#2F6FDE",
  stepNumberInk: "#FFFFFF",
  sender: "#3B5BDB",
  senderInk: "#FFFFFF",
  live: "#2E7D4F",
  liveTint: "#DDEEDF",
  liveInk: "#1F6B3F",
  liveOnDark: "#BFE3C9",
  card: "#FFFFFF",
  cardInk: "#242520",
  cardMuted: "#4A4A42",
  cardLine: "#E7E1D3",
  cardButton: "#D9D2C2",
  stat: "#F6F2E9",
  word: "#FFF4D6",
  tile: "#262823",
  tileLine: "#45473E",
  raised: "#34372E",
  raisedRow: "#45473E",
  track: "#5B5D52",
  quiet: "#8A8A80",
  tickTint: "#DDEEDF",
  tickInk: "#2E7D4F",
  controlsBg: "rgba(251, 248, 241, 0.92)",
  photoFade: "linear-gradient(0deg, rgba(27, 29, 25, 0.85), rgba(27, 29, 25, 0.1))",
} as const;

/** Board 118: the menu's pop-out (Create, Post, Automate) and the Create page. */
export const shellLook = {
  popShadow: "0 18px 50px rgba(40, 34, 20, 0.18)",
  popIcon: "rgba(140, 118, 83, 0.14)",
} as const;

/** Board 118, Home option E (owner's reference, 2 November): the Ask bar, the action pills and the next step card. */
export const homeBoardLook = {
  pill: "#F3EDE1",
  pillLine: "#E6DECD",
  pillDark: "#2A2B26",
  pillLineDark: "#3A3B35",
  askShadow: "0 2px 4px rgba(60, 48, 20, 0.04), 0 14px 36px rgba(60, 48, 20, 0.08)",
  cardShadow: "0 1px 2px rgba(60, 48, 20, 0.04), 0 8px 24px rgba(60, 48, 20, 0.04)",
  next: "#2F3125",
  nextPanel: "#4A4130",
  nextLine: "#6A6C5C",
  nextText: "#FFFDF8",
  nextMuted: "#D6D0C1",
  play: "rgba(255, 253, 248, 0.92)",
  onTint: "#E8EFD9",
  onInk: "#3E5A2C",
} as const;

/** Navy surfaces (sidebar, landing hero, dark editors): the same in light and dark mode. */
export const navy = {
  main: "#1B1D19",
  raised: "#282A24",
  line: "#41443A",
  text: "#FFFFFF",
  textMuted: "#C5C6BB",
  activeBg: "#3D3F35",
  hoverBg: "#32352C",
  /** The icon of the page you're on, and links on navy. */
  activeIcon: "#FFDA83",
  /** STUDIOS / YOU labels in the menu. */
  sectionText: "#AEB2A1",
} as const;

/** Public site extras: the "Soon" label on light cards and on navy. */
export const siteLook = {
  soonText: "#B54708",
  soonFill: "#FFF6ED",
  soonOnNavy: "#FBBF24",
} as const;

export const accent = {
  cyan: "#FFDA83",
  indigo: "#B58527",
  violet: "#805500",
} as const;

/** First run (board 66, step 3): a few starting brand colours to pick from (changed any time in Brand Kit). */
export const starterBrandColors = [
  { hex: "#E6BC62", name: "Gold" },
  { hex: "#3470B5", name: "Blue" },
  { hex: "#C8402E", name: "Red" },
  { hex: "#2F7A4F", name: "Green" },
  { hex: "#6B4FBB", name: "Purple" },
  { hex: "#242520", name: "Charcoal" },
] as const;

/** Davnai's logo (owner's hexagon design): two tones per colour so the facets show; white for the small arrow. */
export const logoColors = {
  charcoal: "#242520",
  charcoalDeep: "#121310",
  charcoalSoft: "#4B4B40",
  ivory: "#FFFDF7",
  goldLight: "#FFE8A6",
  /** The mark's own yellow: the dot on the wordmark's last i (owner decision 2026-10-28). */
  markYellow: "#F8C448",
  silver: "#B8C2CF",
  silverDeep: "#7E8B9D",
  gold: "#E6BC62",
  goldDeep: "#B98A33",
  blue: "#3470B5",
  blueDeep: "#1F4E8A",
  white: "#FFFFFF",
} as const;

/** Fixed colours used by the public product-film mock editor and its mobile fallback. */
export const landingFilm = {
  /** Board 83: the rolling logo rows fade out at both edges (a mask, so only its opacity matters). */
  logoFade: "linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent)",
  panel: "#242620",
  selected: "#403A27",
  timeline: "#20221D",
  clip: "#55584D",
  cut: "#805500",
  ready: "#475C41",
  audio: "linear-gradient(90deg, #6C5A92, #B58527 60%, #476B58)",
  clips: "linear-gradient(90deg, #55584D 0 36%, #805500 36% 48%, #475C41 48%)",
  mobileFade: "linear-gradient(0deg, rgba(27, 29, 25, 0.88) 0%, transparent 58%)",
  videoFade: "linear-gradient(0deg, rgba(10, 12, 10, 0.48), transparent 50%)",
  floating: "rgba(27, 29, 25, 0.82)",
  floatingStrong: "rgba(27, 29, 25, 0.90)",
  floatingSoft: "rgba(27, 29, 25, 0.78)",
  captionShadow: "0 2px 8px rgba(0, 0, 0, 0.90)",
  playerShadow: "0 28px 70px rgba(0, 0, 0, 0.42)",
} as const;

/** Landing page (approved canvas board 33): scheme-aware washes built on the theme's CSS variables. */
/** The premium app look (approved canvas board 34): page headers, cover cards, the bell and Home. */
export const appLook = {
  /** Over a cover picture, so the status chip and title read. */
  coverFade: "linear-gradient(0deg, rgba(15,16,14,.62), rgba(15,16,14,0) 58%)",
  /** Studio tiles on Home: the name sits on the dark bottom of the picture. */
  tileFade: "linear-gradient(0deg, rgba(15,16,14,.88), rgba(15,16,14,.08) 72%)",
  /** A dark glass chip or card on top of a picture. */
  glass: "rgba(27,29,25,.84)",
  glassLine: "rgba(255,255,255,.12)",
  /** Home's photo fades into the card on its left (light, dark). */
  heroFadeLight: "linear-gradient(90deg, #F3EDE1 30%, rgba(243,237,225,.15) 72%)",
  heroFadeDark: "linear-gradient(90deg, #1B1D19 30%, rgba(27,29,25,.2) 72%)",
  heroPaper: "#F3EDE1",
  /** On a phone the words sit over the photo: dark from the bottom up, the face left clear. */
  phoneHeroFade: "linear-gradient(0deg, rgba(27,29,25,.95) 0%, rgba(27,29,25,.9) 38%, rgba(27,29,25,.25) 68%, rgba(27,29,25,0) 82%)",
  photoMask: "linear-gradient(90deg, transparent 0%, #000 32%)",
  /** Photo cards (Create's two ways to start, studio bands): the words sit on the left, the photo fades in on the right. */
  photoPaper: "#F1EADC",
  photoFadePaper: "linear-gradient(90deg, #F1EADC 42%, rgba(241,234,220,.35) 68%, rgba(241,234,220,0))",
  photoFadeNight: "linear-gradient(90deg, #1B1D19 40%, rgba(27,29,25,.35) 70%, rgba(27,29,25,0))",
  photoFadeNightPhone: "linear-gradient(0deg, #1B1D19 46%, rgba(27,29,25,.4) 75%, rgba(27,29,25,0))",
  photoFadePaperPhone: "linear-gradient(0deg, #F1EADC 50%, rgba(241,234,220,.45) 76%, rgba(241,234,220,0))",
  photoMaskRight: "linear-gradient(90deg, transparent 0%, #000 42%)",
  dropLine: "rgba(255,218,131,.45)",
  /** The section you are on, in a studio band's pills: a cream pill with ink words. */
  bandTabOn: "#FFFDF8",
  bandTabInk: "#242520",
  bandTabsBg: "rgba(255,255,255,.07)",
  /** The plan card at the bottom of the sidebar. */
  sidebarCard: "#242620",
  /** Captions over a picture. */
  captionShadow: "0 2px 6px #000",
  /** The soft gold square behind a tool's icon. */
  iconTint: "#FFF3D6",
  /** A new notification (light, dark). */
  unreadLight: "#FFF9EA",
  unreadDark: "rgba(255,218,131,.06)",
  /** The edge of a notification that needs fixing. */
  warnRing: "#D98C8C",
  panelShadow: "0 30px 80px -20px rgba(20,21,18,.45)",
  cardShadow: "0 18px 40px -30px rgba(36,37,32,.35)",
  goldShadow: "0 8px 22px -10px rgba(128,85,0,.55)",
} as const;

export const landingLook = {
  /** Soft green circle behind a tick, light and dark. */
  successTint: "rgba(var(--mui-palette-success-mainChannel) / 0.14)",
  /** Fades the hero scene into the page colour at the bottom. */
  bottomFade: "linear-gradient(0deg, var(--mui-palette-background-default), transparent)",
  /** The solid "Publish to" card on the hero (board 65): a soft lift off the scene. */
  publishShadow: "0 10px 26px -16px rgba(36, 37, 32, 0.5)",
} as const;

/** Gradients: brand (buttons on dark), glow (behind hero content). */
export const gradients = {
  brand: "linear-gradient(135deg, #34372E 0%, #50513D 100%)",
  brandWide: "linear-gradient(120deg, #454736 0%, #805500 100%)",
  /** Gradient text on navy (Home headline): light enough for contrast. */
  onNavyText: "linear-gradient(120deg, #FFDA83 0%, #FFF0C7 100%)",
  glowViolet: "radial-gradient(circle, rgba(181, 133, 39, 0.3) 0%, transparent 70%)",
  glowCyan: "radial-gradient(circle, rgba(255, 218, 131, 0.18) 0%, transparent 65%)",
  heroFade: "linear-gradient(180deg, rgba(27, 29, 25, 0) 0%, #1B1D19 100%)",
} as const;

/** Brand-aligned colours for the code-drawn public landing-page preview. */
export const landingPreview = {
  frame: "linear-gradient(120deg, #07152E 0%, #24356B 48%, #5A347E 100%)",
  atmosphere: "radial-gradient(ellipse at 52% 38%, rgba(34,211,238,.32), transparent 32%), radial-gradient(ellipse at 50% 100%, rgba(124,58,237,.68), transparent 56%), linear-gradient(180deg, transparent 55%, rgba(4,10,25,.84))",
  set: "linear-gradient(160deg, transparent 0 27%, rgba(11,21,49,.68) 28% 34%, transparent 35% 62%, rgba(11,21,49,.55) 63% 69%, transparent 70%)",
  hair: "linear-gradient(145deg, #111528, #28233E 78%)",
  jacket: "linear-gradient(140deg, #8D7AD6, #322D67 75%)",
  skin: "linear-gradient(135deg, #D89C83, #87536C 82%)",
  laptop: "linear-gradient(145deg, #34405D, #11182B 72%)",
  subjectGlow: "0 -14px 50px rgba(147,197,253,.2)",
  faceGlow: "0 0 34px rgba(240,171,252,.2)",
  laptopShadow: "0 0 24px rgba(3,8,23,.35)",
  textShadow: "0 1px 8px rgba(0,0,0,.8)",
  photoFade: "linear-gradient(0deg, rgba(7,21,46,.72), transparent 46%)",
} as const;

/**
 * The landing page's photo demos (hero loop, before/after slider, Social Studio phones): how one plain frame is shown
 * raw and then finished. The "after" is drawn with filters and overlays on the same photo, as an illustration.
 */
export const landingVisuals = {
  rawFilter: "saturate(0.3) brightness(0.8) contrast(0.82)",
  captionedFilter: "saturate(0.45) brightness(0.86) contrast(0.88)",
  finishedFilter: "saturate(1.15) contrast(1.08)",
  finishedGrade: "radial-gradient(ellipse at 60% 40%, transparent 45%, rgba(4,10,25,.55) 100%), linear-gradient(120deg, rgba(79,70,229,.18), rgba(124,58,237,.12))",
  letterbox: "#000000",
  rawLabel: "rgba(0,0,0,0.6)",
  rawText: "#E7E9F4",
  shortsFade: "linear-gradient(0deg, rgba(0,0,0,.55), transparent 35%)",
  cardFade: "linear-gradient(0deg, rgba(7,21,46,.92) 0%, rgba(7,21,46,.4) 38%, transparent 60%)",
  cardText: "#D5DBEA",
  handleGlow: "0 0 18px rgba(124,58,237,0.8)",
  wordDim: "rgba(255,255,255,0.55)",
  wordNext: "#C4B5FD",
  stepActiveBg: "rgba(124,58,237,0.30)",
  stepActiveBorder: "rgba(196,181,253,0.65)",
  bestTimeText: "#DDD6FE",
  phoneShadow: "0 24px 50px rgba(0,0,0,0.4)",
  postShadow: "0 24px 50px rgba(0,0,0,0.35)",
  thumbFade: "linear-gradient(90deg, rgba(7,21,46,.85) 0%, rgba(7,21,46,.35) 55%, transparent 80%)",
  thumbBadge: "rgba(0,0,0,0.8)",
} as const;

/** Soft elevation for cards and floating panels. */
export const shadows = {
  card: "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)",
  cardHover: "0 2px 4px rgba(36, 37, 32, 0.06), 0 16px 40px rgba(128, 85, 0, 0.10)",
  mobileFloat: "0 12px 28px rgba(7, 21, 46, 0.22)",
  float: "0 24px 60px rgba(7, 21, 46, 0.45)",
  button: "0 8px 20px rgba(128, 85, 0, 0.16)",
  /** The chosen tab in a pill bar. */
  tab: "0 1px 2px rgba(16, 24, 40, 0.10)",
  /** Bars that float over content (e.g. "Make my video"). */
  bar: "0 12px 32px rgba(16, 24, 40, 0.14)",
  /** A panel floating over the page (the Ask Davnai chat). */
  panel: "0 24px 64px rgba(7, 21, 46, 0.22), 0 2px 8px rgba(7, 21, 46, 0.08)",
} as const;

/** Icon tiles on Create / dashboard: soft tinted squares, one hue per kind of content. */
export const tints = {
  video: { bg: "#FFF4D8", fg: "#805500" },
  image: { bg: "#EDF2E7", fg: "#415536" },
  post: { bg: "#E8F8FB", fg: "#0E7490" },
  blog: { bg: "#EAF6F0", fg: "#047857" },
  audio: { bg: "#FFF4E5", fg: "#B45309" },
  transcript: { bg: "#FDEEF6", fg: "#BE185D" },
} as const;
export type Tint = keyof typeof tints;

/** Burned-in caption samples: drawn like the worker draws them (white words, black outline, yellow highlight). */
export const captionLook = {
  text: "#FFFFFF",
  highlight: "#FFD60A",
  outline: "0 0 2px #000, 0 0 2px #000, 1px 1px 2px #000, -1px -1px 2px #000",
  softShadow: "0 1px 3px rgba(0,0,0,.9)",
  box: "rgba(0,0,0,0.65)",
  dim: "linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.35))",
  backdrop: "linear-gradient(160deg, #3a4150 0%, #1d2129 100%)",
  focusShade: "0 0 0 999px rgba(0,0,0,0.08)",
  iconShadow: "drop-shadow(0 1px 2px rgba(0,0,0,.5))",
  /** The captions library (board 40): text on the word colour's own box, and a shadow under the words. */
  onAccent: "#111111",
  shadow: "0 2px 6px rgba(0,0,0,.7)",
} as const;

/** Neutral picture served in place of a thumbnail when the viewer isn't signed in. */
export const thumbnailPlaceholder = { background: "#E9ECF5", mark: "#9AA3B8" } as const;

/**
 * The YouTube thumbnail designer: text colours people can pick (their Brand Kit colours are added in front), the
 * outline and shadow behind words, the dark fade over the picture, and the duration badge in previews.
 */
export const thumbnailLook = {
  white: "#FFFFFF",
  ink: "#0B0F19",
  gold: "#E6BC62",
  yellow: "#FFD60A",
  red: "#E5262B",
  blue: "#3470B5",
  indigo: "#4F46E5",
  green: "#22C55E",
  outline: "#000000",
  shadow: "rgba(0, 0, 0, 0.65)",
  fade: "rgba(4, 7, 15, 0.82)",
  fadeClear: "rgba(4, 7, 15, 0)",
  grey: "rgba(10, 12, 20, 0.35)",
  badge: "rgba(0, 0, 0, 0.72)",
  badgeText: "#FFFFFF",
  empty: "#1B2236",
} as const;

/** Designs drawn without AI (Ask Davnai, connected apps): colour choices besides the person's Brand Kit colours. */
export const designLook = {
  indigo: "#4F46E5",
  violet: "#7C3AED",
  navy: "#07152E",
  ink: "#0B0F19",
  white: "#FFFFFF",
  cream: "#FFF8EC",
  yellow: "#FCD34D",
  coral: "#FF6B57",
  green: "#22C55E",
  sky: "#38BDF8",
  /** Over a photo: a dark wash so the words stay readable. */
  wash: "rgba(4, 7, 15, 0.55)",
  washClear: "rgba(4, 7, 15, 0)",
} as const;

export const typography = {
  editorialFamily: '"Davnai Editorial", Georgia, "Times New Roman", serif',
  fontFamily: '"Inter Variable", Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  headingFamily: 'Poppins, "Inter Variable", Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  /** The gold italic phrase in page titles (Instrument Serif, as on the landing page). */
  accentFamily: '"Davnai Accent", Georgia, serif',
} as const;

/** 8px spacing grid; radius in px. */
export const shape = { radius: 8, controlRadius: 10, softRadius: 12, cardRadius: 16, tileRadius: 18, illustrationRadius: 20, pillRadius: 999 } as const;

export type Scheme = keyof typeof palette;

/** Brand Kit: the checkerboard behind a logo (shows transparency) and the first colour offered when adding one. */
/** Recording Studio backgrounds (canvas drawing): the empty frame before a picture loads, and the soft top light on
 * a brand-colour background. */
export const recordBackdropLook = { empty: "#1B1D19", light: "#FFFFFF" } as const;

export const brandKitLook = { checkerA: "#FFFFFF", checkerB: "#E9ECF3", newColor: "#4F46E5" } as const;

/**
 * Call to action animation (previewed in the browser; drawn by the worker in workers/media/davnai_media/cta.py with
 * the same colours). The button colour is the person's Brand Kit colour, else Davnai indigo.
 */
/** The watermark's text colours (board 30); the worker draws them the same. */
export const watermarkLook = { white: "#FFFFFF", black: "#000000" } as const;

export const ctaLook = {
  defaultColor: "#4F46E5",
  card: "#FFFFFF",
  shadow: "rgba(0, 0, 0, 0.35)",
  text: "#18181C",
  muted: "#6E6E76",
  done: "#E5E5EA",
  onLight: "#141418",
  onDark: "#FFFFFF",
  stage: "#1F2A3A",
  /** Behind a platform logo that is white on its own (Threads, TikTok). */
  plate: "#000000",
} as const;

/**
 * Animated intros/outros (Remotion, src/remotion): starting colours for concepts before the person's Brand Kit or
 * choices replace them, and the fixed shading the pieces use. Drawn the same in the studio and by the worker.
 */
export const motionLook = {
  bgFrom: "#13265A",
  bgTo: "#070F24",
  bgDefault: "#0B1A3A",
  text: "#FFFFFF",
  soft: "#C9D2EE",
  accent: "#E9B949",
  subscribe: "#DC2626",
  nameText: "#C7CCE0",
  calloutText: "#0B1020",
  namePanel: "rgba(11, 16, 32, 0.86)",
  glow: "rgba(255, 255, 255, 0.18)",
  sweep: "rgba(255, 255, 255, 0.10)",
  textShadow: "0 2px 18px rgba(0, 0, 0, 0.35)",
  pieceShadow: "0 8px 30px rgba(0, 0, 0, 0.35)",
} as const;

/**
 * Davnai Motion pack: the designed intros, titles, text on the video and end screens. Grounds, inks and the default
 * accents of each look (the person's Brand Kit colour replaces the accent). Used only by src/remotion/looks.
 */
export const motionPack = {
  night: "#050608",
  ink: "#0B0B0F",
  studio: "#0C0D10",
  slate: "#1A1C22",
  paper: "#F4EEE4",
  paperInk: "#1B1A17",
  /** Second line of words on paper and cream (Light background on intros and outros). */
  paperSoft: "#5E574C",
  cream: "#FBF6EE",
  sand: "#E9DFD2",
  muted: "#CBBFB0",
  grey: "#A9A39B",
  white: "#FFFFFF",
  black: "#000000",
  gold: "#E8B86B",
  amber: "#FFB86B",
  steel: "#5BD1FF",
  flare: "#FFF2DC",
  streak: "#FFF6E8",
  yellow: "#FFD23F",
  coral: "#FF4D2E",
  violet: "#7B61FF",
  /** Clean corporate: deep navy ink on cool light grey. */
  navy: "#13233A",
  frost: "#F5F7FA",
  mist: "#DDE4EC",
  teal: "#2EC4B6",
} as const;


/**
 * Davnai Motion styles (Round D): each channel style's own colours for its matching set (name titles, key points,
 * quotes, counters, cards between parts and, for the newer styles, intro and outro). `ground` fills full-screen
 * cards; `panel` sits behind words on the video; `ink`/`soft` are the words; `accent` is the default accent (the
 * Brand Kit colour replaces it). Used only by src/remotion/looks and lib/motion/looks.
 */
export const motionSkins = {
  noir: { ground: "#050608", panel: "#0B0C0F", ink: "#FBF6EE", soft: "#CBBFB0", accent: "#FFB86B" },
  bold: { ground: "#0B0B0F", panel: "#0B0B0F", ink: "#FFFFFF", soft: "#D4D4DC", accent: "#FFD23F" },
  editorial: { ground: "#F4EEE4", panel: "#F4EEE4", ink: "#1B1A17", soft: "#5E574C", accent: "#FF4D2E" },
  studio: { ground: "#1A1C22", panel: "#23262E", ink: "#FFFFFF", soft: "#B3B7C2", accent: "#FF4D2E" },
  corporate: { ground: "#F4F5F7", panel: "#FFFFFF", ink: "#16181D", soft: "#5B6070", accent: "#3D63DD" },
  kinetic: { ground: "#7B61FF", panel: "#7B61FF", ink: "#FFFFFF", soft: "#ECE7FF", accent: "#FFD23F" },
  tech: { ground: "#0A1020", panel: "#0E1628", ink: "#E8F1FF", soft: "#8FA3C4", accent: "#3DF5C8" },
  luxe: { ground: "#10241C", panel: "#132B21", ink: "#F6EFE2", soft: "#C9BFA9", accent: "#D8B46A" },
} as const;

/**
 * Social Studio: each platform's colour (used around its official logo, which comes from public/brand/platforms)
 * and the account states.
 */
export const socialLook = {
  youtube: "#E5322D",
  facebook: "#1877F2",
  instagram: "#C13584",
  linkedin: "#0A66C2",
  tiktok: "#25262B",
  threads: "#101010",
  bluesky: "#1185FE",
  pinterest: "#E60023",
  snapchat: "#FFFC00",
  /** Telegram's blue, behind its letters until Telegram's own logo file is added. */
  telegram: "#26A5E4",
  letters: "#FFFFFF",
  /** Letters on light tiles (Snapchat's yellow). */
  lettersDark: "#101010",
  /** Black plate behind TikTok's and Threads' white icons (their own app-icon look), so they show on light backgrounds. */
  tiktokPlate: "#000000",
} as const;

/** The video editor's transitions (approved canvas board 35): the warm glow of a light leak, the dips' colours. */
export const transitionLook = {
  leak: "radial-gradient(circle at 70% 30%, rgba(255,190,90,.9), rgba(255,150,60,.35) 45%, transparent 70%)",
  black: "#000000",
  white: "#FFFFFF",
  glitch: "linear-gradient(0deg, transparent 30%, rgba(255,0,90,.35) 30%, rgba(255,0,90,.35) 40%, transparent 40%, transparent 62%, rgba(0,220,255,.3) 62%, rgba(0,220,255,.3) 70%, transparent 70%)",
  tileA: "linear-gradient(135deg, #3A3C34, #1B1D19)",
  tileB: "linear-gradient(135deg, #FFDA83, #805500)",
  /** Film burn (board 39): a hot orange flare from a corner. */
  burn: "radial-gradient(circle at 25% 70%, rgba(255,120,40,.95), rgba(120,20,0,.6) 35%, transparent 65%)",
  /** Your brand's transitions without brand colours (the worker uses the same). */
  brandInk: "#242520",
  /** The name drawn when there's no logo, on a light or a dark brand colour. */
  nameOnDark: "#FFFFFF",
  nameOnLight: "#141414",
  /** The transition engine's new looks (board 64): the stage behind a picture shrunk into a box or card, the box's
   *  edge, its shadow, a number or quote card, and the highlight on it. */
  stage: "#191A17",
  stageSoft: "#2C2D27",
  frameLine: "#F7F3E9",
  shadow: "rgba(0,0,0,.45)",
  card: "#242520",
  highlight: "#FFDA83",
  ribbon: "rgba(25,26,23,.88)",
} as const;

/** The editor's one live preview (board 35): the frame behind everything, and the chip while a clip loads. */
export const previewLook = {
  black: "#000000",
  white: "#FFFFFF",
  chip: "rgba(0,0,0,.62)",
  /** Explain it (board 37): behind you when there's no brand colour (the worker's DEFAULT_COLOR). */
  explainBack: "#1B2A4A",
  /** Colour → Vignette (board 42): the darkened edge, faded in by the slider. */
  vignette: "radial-gradient(ellipse at center, rgba(0,0,0,0) 45%, rgba(0,0,0,.85) 100%)",
} as const;

/** Cutting by words (approved canvas board 35): picked words, and the lines through cut and marked words. */
/** Board 62: the Director's Words lines (the line being played, graphic and b-roll marks, a found word). */
export const wordsLinesLook = {
  playing: "var(--mui-palette-secondary-main)",
  graphic: "#7FB3FF",
  broll: "#C9A0FF",
  found: "rgba(var(--mui-palette-secondary-mainChannel) / 0.3)",
  fixed: "#8FD4A8",
} as const;

export const wordsLook = {
  pick: "rgba(var(--mui-palette-secondary-mainChannel) / 0.28)",
  pickDark: "rgba(255,218,131,.28)",
  line: "var(--mui-palette-secondary-main)",
  lineDark: "#FFDA83",
  repeat: "var(--mui-palette-error-main)",
  repeatDark: "#D98C8C",
} as const;

/** Stock results (approved canvas board 36): the badges on a picture, its hover dim, and the play button. */
export const stockLook = {
  badge: "rgba(0,0,0,.66)",
  dim: "rgba(0,0,0,.28)",
  play: "rgba(255,255,255,.92)",
  track: "rgba(255,255,255,.3)",
} as const;

/** Colour → Colour by colour and Curves (from the CapCut review): the six colour dots and each curve's line. */
export const colourToolLook = {
  red: "#E5484D",
  yellow: "#F5C542",
  green: "#3DBE6B",
  cyan: "#2BC4D9",
  blue: "#3E7BFA",
  magenta: "#D24BD9",
  grid: "rgba(127,127,127,.25)",
  diagonal: "rgba(127,127,127,.45)",
} as const;

/** Effects on your video (from the CapCut review): the light leak's warm glow and the old tape's lines. */
/** Change my background (owner, 2026-10-24): the colours offered behind you (the first is the default). */
export const backgroundSwap = {
  defaultColor: "#1F2937",
  swatches: ["#1F2937", "#0F172A", "#F8FAFC", "#1E8A5A", "#2563EB", "#7C3AED", "#DB2777", "#F59E0B"],
} as const;

export const effectLook = {
  /** The glow's colour as "r,g,b" (the worker uses the same: workers/media/davnai_media/effects.py). */
  leakRgb: "255,138,61",
  /** The glow at `x` (share of the width) and strength `a`, screened over the video in the preview. */
  leakGlow: (x: number, a: number) => `radial-gradient(circle at ${(x * 100).toFixed(1)}% 30%, rgba(255,138,61,${a}) 0%, rgba(255,138,61,0) 60%)`,
  scanlines: "repeating-linear-gradient(to bottom, rgba(0,0,0,.9) 0px, rgba(0,0,0,.9) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 4px)",
} as const;

/**
 * Automations (approved canvas board 67): the builder's conversation preview, drawn plain (a phone frame, the
 * person's grey bubbles, the creator's own gold bubble, buttons as outlined boxes; never a platform's look), and the
 * soft badges in the list.
 */
export const automationLook = {
  frame: "#1B1D19",
  frameShadow: "0 30px 60px -30px rgba(36,37,32,.6)",
  panel: "#FFFFFF",
  ink: "#1B1D19",
  muted: "#65655C",
  bubble: "#F0F0F0",
  line: "#DADADA",
  mine: "#926300",
  mineInk: "#FFFFFF",
  link: "#805500",
  stepBadge: "#242520",
  stepBadgeInk: "#FFDA83",
  chip: "#FFF4D6",
  chipInk: "#242520",
  tagChip: "#E4ECF4",
  tagChipInk: "#2C4A66",
  focusBorder: "#E6BC62",
  /** Board 84, the builder's Flow view: the dotted map, the lines between boxes, the chosen box's ring and end boxes. */
  flowMap: "#FBF8F1",
  flowMapDark: "#1A1C18",
  flowDot: "#DCD5C5",
  flowDotDark: "#34372E",
  flowEdge: "#8A8A80",
  flowRing: "#FFF1C9",
  flowRingDark: "#4A3F22",
  flowEnd: "#F6F2E9",
  flowEndDark: "#2A2C27",
  flowLabel: "#FBF8F1",
  flowLabelDark: "#262823",
  /** Boards 75 to 78: a question's card, the "check their answers" box and the AI box (light, then dark). */
  soft: "#FFF9EC",
  softLine: "#F0E2BF",
  softDark: "#2E2818",
  softLineDark: "#4A3F22",
  /** Saved answers (Contacts, Messages): "Interest: Group course". */
  answer: "#FFF4D6",
  answerInk: "#5C3D00",
  answerDark: "#3A3020",
  answerInkDark: "#FFE7A8",
  /** Messages (board 76): needs you, Davnai answering, the unread count. */
  needs: "#FDE4D6",
  needsInk: "#8A2E00",
  needsDark: "#4A2A1C",
  needsInkDark: "#FFC8A8",
  live: "#DDEEDF",
  liveInk: "#1F6B3F",
  liveDark: "#1F3325",
  liveInkDark: "#8FD19E",
  count: "#C62828",
  countInk: "#FFFFFF",
  /** Messages (board 76): their bubbles, and yours when you reply yourself (Davnai's are mine/mineInk). */
  theirs: "#E7E1D3",
  theirsInk: "#242520",
  theirsDark: "#34372E",
  theirsInkDark: "#F1EEE6",
  yours: "#242520",
  yoursInk: "#FFFFFF",
  yoursDark: "#F1EEE6",
  yoursInkDark: "#1B1D19",
  avatar: "#E7E1D3",
  avatarInk: "#4A4A42",
  avatarDark: "#3A3B33",
  avatarInkDark: "#F1EEE6",
  /** Board 77: the QR code, black on white in both modes (what scanners expect). */
  qrDark: "#000000",
  qrLight: "#FFFFFF",
  /** The "not sure" match on the AI board and the dashed button chip. */
  dashed: "#B8862B",
  /** Board 102: Start from a template on the Automations home (its warm band and edge), light and dark. */
  templateBand: "#FFF8E6",
  templateBandDark: "#2E2818",
  templateEdge: "#EBD9A8",
  templateEdgeDark: "#4A3F22",
} as const;

/**
 * Analytics, board 104 (approved): the gold band for what Davnai's automations did (kept apart from the platform's own
 * numbers), its cards and keyword chips, and the "new" outline; light and dark.
 */
export const analyticsLook = {
  band: "#FFF6DC",
  bandDark: "#2B2414",
  edge: "#D9B25A",
  edgeDark: "#6B5526",
  card: "#FFFDF8",
  cardDark: "#1F1B12",
  cardEdge: "#E8D29A",
  cardEdgeDark: "#4A3F22",
  ink: "#5C4300",
  inkDark: "#F2D58C",
  chip: "#2A2116",
  chipInk: "#F7D98A",
  markPlate: "#2A2116",
  /** The audience gender bar's three parts. */
  women: "#7A5600",
  men: "#D9B25A",
  other: "#CFC8B6",
  tip: "#FFF4D6",
  tipDark: "#3A2F14",
  tipInk: "#3D2A00",
  tipInkDark: "#F7E3AE",
  storyBadge: "rgba(0,0,0,.6)",
  onPhoto: "#FFFFFF",
} as const;

/** Plan and billing → Automation contacts (approved canvas board 70): the card's border, bars, the extra-contacts tile and Buy's choices. */
export const contactsLook = {
  border: "#E6BC62",
  bar: "#926300",
  barDark: "#FFDA83",
  track: "#EFE9DB",
  trackDark: "#3A3B33",
  extra: "#FFF4D6",
  extraInk: "#5C3D00",
  extraDark: "#2E2818",
  extraInkDark: "#FFE7A8",
  picked: "#FFF8E6",
  pickedDark: "#2E2818",
  save: "#E6F2E9",
  saveInk: "#2E6B3F",
  saveDark: "#1F3325",
  saveInkDark: "#8FD19E",
  /** Automations page notices: running low, all used up. */
  low: "#FFF4D6",
  lowDark: "#2E2818",
  out: "#F6E3DF",
  outInk: "#4A1A12",
  outDark: "#3A2420",
  outInkDark: "#F2C6BD",
} as const;

/** Accounts → Telegram (approved canvas board 69): the dark "What your bot can do" card, the tip and the statuses, light and dark. */
export const telegramLook = {
  dark: "#1B1D19",
  darkInk: "#FFFFFF",
  darkMuted: "#C5C6BB",
  darkAccent: "#FFDA83",
  tip: "#FFF4D6",
  tipDark: "#2E2818",
  ready: "#2E6B3F",
  readyDark: "#8FD19E",
  waiting: "#805500",
  waitingDark: "#FFDA83",
  code: "#F3EEE2",
  codeDark: "#2A2B25",
} as const;

/** Automations Contacts, Results and Activity (approved canvas board 67, screens 3 to 5): chips, highlighted rows and bars, light and dark. */
export const automationViews = {
  tagChip: "#E4ECF4",
  tagChipInk: "#2C4A66",
  tagChipDark: "#22313F",
  tagChipInkDark: "#BFD3E6",
  plainChip: "#EFE9DB",
  plainChipDark: "#34352E",
  stopChip: "#F6E3DF",
  stopChipInk: "#7A2E22",
  stopChipDark: "#3A2420",
  stopChipInkDark: "#F2C6BD",
  needsRow: "#FFF8E6",
  needsRowDark: "#2E2818",
  tip: "#FFF4D6",
  tipDark: "#2E2818",
  sent: "#2E6B3F",
  sentDark: "#8FD19E",
  notSent: "#7A2E22",
  notSentDark: "#F2A595",
  /** The funnel's six bars, darkest to lightest (comment → email). */
  funnel: ["#242520", "#4A4A42", "#805500", "#926300", "#E6BC62", "#FFDA83"],
  funnelDark: ["#E9E6DC", "#B9B6AA", "#C58A1F", "#926300", "#E6BC62", "#FFDA83"],
  day: "#D8D0BD",
  dayDark: "#4A4A42",
  dayPeak: "#926300",
  versionA: "#926300",
  versionB: "#4A4A42",
  versionBDark: "#B9B6AA",
  track: "#EFE9DB",
  trackDark: "#34352E",
} as const;

/** Home (approved canvas board 68): the two paths' buttons (charcoal Upload, peach Record with a red dot). */
export const homePathLook = {
  upload: "#1E1F1B",
  uploadInk: "#FFFFFF",
  record: "#FCE9DF",
  recordDark: "#3A2A24",
  recordInk: "#242520",
  recordInkDark: "#FCE9DF",
  recordDot: "#D7263D",
  automateSoft: "#3D3F35",
  /** The photo fades in from the left behind the two paths. */
  photoMask: "linear-gradient(90deg, transparent, #000 55%)",
  /** First run: the soft ring around a chosen card. */
  choiceRing: "rgba(230,188,98,.25)",
  /** Needs you on a laptop: one line that scrolls sideways, fading at the right edge so more is clearly there. */
  needsFade: "linear-gradient(90deg, #000 92%, transparent)",
} as const;

/**
 * Charts (approved boards 84 and 85): up to four series that differ in lightness as well as hue, the area under a
 * line, the empty part of a ring, and gridlines, each with its dark-mode value.
 */
export const chartLook = {
  s1: "#805500",
  s1Dark: "#FFDA83",
  s2: "#E6BC62",
  s2Dark: "#B98A2E",
  s3: "#2F6FDE",
  s3Dark: "#7EA6F0",
  s4: "#B9B6AA",
  s4Dark: "#6E6F63",
  area: "#FFE9B0",
  areaDark: "rgba(255,218,131,.16)",
  track: "#F1EEE6",
  trackDark: "#34372E",
  good: "#2E7D4F",
  goodDark: "#7FCB98",
  grid: "#E7E1D3",
  gridDark: "#414239",
} as const;

/** Board 98: the round, movable Ask Davnai button and its one-time hint. */
export const askLook = {
  shadow: "0 10px 30px rgba(36,37,32,0.3)",
  glow: "0 0 0 8px rgba(255,218,131,0.55), 0 10px 30px rgba(36,37,32,0.3)",
  edge: "rgba(255,255,255,0.12)",
  hintShadow: "0 12px 30px rgba(36,37,32,0.16)",
} as const;

/** Board 103: the phones, chat and editor drawn on the website (the same in light and dark, as real phones are). */
export const phoneLook = {
  frame: "#1B1C18",
  screen: "#FFFFFF",
  ink: "#111111",
  muted: "#6E6E64",
  faint: "#8A8A7E",
  bubble: "#F1EEE7",
  mine: "#7A5600",
  mineInk: "#FFFFFF",
  quickEdge: "#D9CFB8",
  line: "#EEE9DE",
  cardEdge: "#E7DFCD",
  gold: "#E2B04A",
  goldInk: "#1E1F1A",
  arrow: "#C99A3A",
  like: "#E8344E",
  reelsShade: "linear-gradient(180deg, rgba(0,0,0,.28), rgba(0,0,0,0) 30%, rgba(0,0,0,0) 58%, rgba(0,0,0,.6))",
  reelsBar: "#000000",
  onPhoto: "#FFFFFF",
  shadow: "0 24px 50px rgba(40,30,10,.18)",
  floatShadow: "0 12px 30px rgba(40,30,10,.14)",
  cardShadow: "0 14px 30px rgba(40,30,10,.10)",
  approved: "#E5F2E9",
  approvedInk: "#2E6B3F",
  guideCover: "#EFE4C9",
  guideInk: "#3A2A08",
  playDisc: "rgba(255,255,255,.88)",
  badge: "rgba(0,0,0,.65)",
  tint: "#F8E7BD",
  warmBand: "#FDF3DC",
  warmBandDark: "#2E2818",
  badgeTint: "#FCEFD3",
  badgeEdge: "#F0DDB0",
  badgeInk: "#5A4A22",
  dark: "#1E1F1A",
  darkInk: "#FFFFFF",
  darkMuted: "#D7D3C8",
  darkFaint: "#BDB9AE",
  darkEdge: "#4A4B44",
  ok: "#2E7D4F",
  okTint: "#EAF5EC",
  okTintDark: "#1F3325",
  live: "#2E9E5B",
  waitDot: "#CFC8B6",
  editor: "#1F201C",
  editorLine: "#34352F",
  editorInk: "#E9E6DD",
  editorMuted: "#CFCBC0",
  editorFaint: "#8F8B80",
  editorCut: "#4A2A26",
  editorCutInk: "#E5A79C",
  editorCutTime: "#C9A39C",
  editorActive: "#3A3320",
  editorGold: "#F2C35B",
  editorRaised: "#2A2B26",
  editorCaption: "rgba(0,0,0,.72)",
  editorShadow: "0 24px 50px rgba(40,30,10,.22)",
  wave: "repeating-linear-gradient(90deg, #C99A3A 0 2px, transparent 2px 5px)",
} as const;

/** Board 108: contact stages, lightest to strongest (they differ in lightness, not hue alone). */
export const stageLook = {
  new: { bg: "#F3EFE6", ink: "#4A4A40", bgDark: "#2E2F29", inkDark: "#D9D6CC" },
  engaged: { bg: "#EFE7D3", ink: "#4A4330", bgDark: "#35322A", inkDark: "#E3DCC8" },
  interested: { bg: "#F2DFAE", ink: "#4A3A10", bgDark: "#4A3D1C", inkDark: "#F2DFAE" },
  lead: { bg: "#E6BC62", ink: "#2E2000", bgDark: "#B88A2E", inkDark: "#1B1400" },
  customer: { bg: "#242520", ink: "#FFFDF8", bgDark: "#EDE8DA", inkDark: "#1B1D19" },
  quiet: { bg: "transparent", ink: "#5A5A50", bgDark: "transparent", inkDark: "#BDB9AE", line: "#C9BFA8" },
} as const;

/** Board 113: engagement levels (how lively someone is now); they differ in lightness too, and cooling is warm. */
export const levelLook = {
  active: { bg: "#E6BC62", ink: "#2E2000", bgDark: "#B88A2E", inkDark: "#1B1400" },
  highly: { bg: "#242520", ink: "#E6BC62", bgDark: "#EDE8DA", inkDark: "#5C3D00" },
  cooling: { bg: "#F6E3D3", ink: "#6B3300", bgDark: "#4A3222", inkDark: "#F6D8BF" },
  inactive: { bg: "#ECEAE4", ink: "#4F4F46", bgDark: "#33342E", inkDark: "#CFCBC0" },
  bar: "#E6BC62",
  barEmpty: "#E2DBCB",
  barEmptyDark: "#3A3B35",
} as const;

/** Board 110: the Calendar's Plan view: ideas are dashed gold cards; each theme has its own soft colour. */
export const planLook = {
  ideaLine: "#E6BC62",
  ideaBg: "#FFFBF0",
  ideaBgDark: "rgba(230,188,98,.10)",
  ideaInk: "#805500",
  ideaInkDark: "#E6BC62",
  /** Board 114: a week's filming day. */
  filmBg: "#F2DFAE",
  filmInk: "#3D2E00",
  filmBgDark: "#4A3D1C",
  filmInkDark: "#F2DFAE",
  ready: "#2F6B3A",
  readyDark: "#8FD19E",
  themes: [
    { bg: "#E8F0E3", ink: "#2C3A24", bgDark: "#2B3527", inkDark: "#D3E4C8" },
    { bg: "#EDE6F3", ink: "#3A2C48", bgDark: "#332B3B", inkDark: "#E0D3EC" },
    { bg: "#FFF0D1", ink: "#4A3A10", bgDark: "#3F351C", inkDark: "#F2DFAE" },
    { bg: "#E3EEF5", ink: "#1F3646", bgDark: "#25323B", inkDark: "#CFE2EF" },
    { bg: "#F5E6E3", ink: "#4A2A24", bgDark: "#3B2B28", inkDark: "#EFD4CE" },
    { bg: "#EEEDE6", ink: "#3A3A33", bgDark: "#32322C", inkDark: "#DCDACF" },
  ],
} as const;
